import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { catchError, finalize, of, switchMap, tap } from 'rxjs';

import { LogoutEventWatcherService } from '@core/auth/logout-event-watcher.service';
import { CreateMarketplaceAccountRequest } from '@core/models/marketplace-account-model';
import { CacheBusterService } from '@core/services/cache-buster.service';
import { CustomToastService } from '@core/services/custom-toast.service';
import { EbayAuthApiService } from '@core/services/ebay-auth-api.service';
import { MarketplaceAccountApiService } from '@core/services/marketplace-account-api.service';

import { ListerHomeComponent } from '@features/shell/lister-home/lister-home.component';

/** Where eBay sends the user back after they authorize, or refuse, a marketplace account. */
const EBAY_CALLBACK_PATH = '/marketplaces/accounts';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  imports: [ListerHomeComponent]
})
export class AppComponent implements OnInit {
  private logoutEventWatcher = inject(LogoutEventWatcherService);

  private toast = inject(CustomToastService);
  private router = inject(Router);
  private marketplaceAccountApiService = inject(MarketplaceAccountApiService);
  private route = inject(ActivatedRoute);
  private EbayAuthService = inject(EbayAuthApiService);
  private cacheService = inject(CacheBusterService);

  // The text of each toast; an empty string hides it. CustomToastService clears them when they expire.
  successMsg = '';
  errorMsg = '';
  processMsg = '';

  // Ebay Token Saver Logic
  EbayCode: string | null = null;
  accountId: number | 0 = 0;
  marketplaceAccount: CreateMarketplaceAccountRequest = new CreateMarketplaceAccountRequest();

  ngOnInit() {
    this.cacheService.init();

    this.toast.successMessage$.subscribe(msg => (this.successMsg = msg));
    this.toast.errorMessage$.subscribe(msg => (this.errorMsg = msg));
    this.toast.processMessage$.subscribe(msg => (this.processMsg = msg));

    this.checkEbayCode();

    this.logoutEventWatcher.start();
  }

  dismissToast(kind: 'success' | 'error' | 'process') {
    if (kind === 'success') {
      this.successMsg = '';
    } else if (kind === 'error') {
      this.errorMsg = '';
    } else {
      this.processMsg = '';
    }
  }

  checkEbayCode() {
    this.route.queryParams
      .pipe(
        switchMap(params => {
          const ebayError = params['error'];
          // eBay sends its callback to the marketplace accounts page; an error parameter elsewhere is not eBay's.
          if (ebayError && this.isMarketplaceAccountsPage()) {
            const description = params['error_description'] ?? ebayError;
            console.error('eBay authorization error:', ebayError, description);
            this.toast.error(`eBay authorization failed: ${description}`);
            this.clearEbayParams();
            return [];
          }

          this.EbayCode = params['code'];
          this.accountId = Number(params['state']);

          if (!this.EbayCode || !this.accountId) {
            return []; // Return an empty observable to prevent further execution
          }

          return this.exchangeEbayCode();
        }),
        catchError(error => {
          console.error(error);
          return []; // Return an empty observable to prevent further execution
        })
      )
      .subscribe();
  }

  exchangeEbayCode() {
    const encodedCode = encodeURIComponent(this.EbayCode!);
    // The success or error toast below replaces this message.
    this.toast.process('Authorizing your eBay account...');

    return this.EbayAuthService.exchangeCode(encodedCode, this.accountId).pipe(
      switchMap(Auth => {
        if (!Auth.accessToken || !Auth.refreshToken) {
          throw new Error('Invalid Token Data');
        }

        return this.marketplaceAccountApiService
          .authorize(this.accountId, {
            accessToken: Auth.accessToken,
            refreshToken: Auth.refreshToken,
            accessTokenExpireAt: Auth.accessTokenExpiry,
            refreshTokenExpireAt: Auth.refreshTokenExpiry
          })
          .pipe(
            tap(res => {
              if (res?.message) {
                this.toast.success(res.message);
              } else {
                this.toast.success('Marketplace account authorized.');
              }
            })
          );
      }),
      catchError(error => {
        console.error(error);
        const message =
          error?.error?.detail ||
          error?.error?.message ||
          error?.error?.error?.message ||
          'Could not authorize the eBay account. Try again from Marketplace accounts.';
        this.toast.error(message);
        return of(null);
      }),
      finalize(() => {
        // Replaces the callback URL, so a refresh does not exchange the same code again.
        this.router.navigate([EBAY_CALLBACK_PATH], { replaceUrl: true });
      })
    );
  }

  private isMarketplaceAccountsPage(): boolean {
    return this.router.url.split(/[?#]/)[0] === EBAY_CALLBACK_PATH;
  }

  /** Drops the eBay callback query string from the current URL, so a refresh does not repeat it. */
  private clearEbayParams() {
    this.router.navigate([], { queryParams: {}, replaceUrl: true });
  }
}
